import React, { useEffect, useState } from "react";
import { LoadingIcon } from "../../assets/icons/Icons";
import FormSection from "../form/Section";
import FormHeader from "../form/Header";
import Popup from "../Popup";
import CustomSelect from "../form/CustomSelect";
import ReportMethodSelect from "../form/ReportMethodSelect";
import CustomTooltip from "../CustomTooltip";
import { useTranslation } from "react-i18next";
import ProgramReportTypeSelect from "../form/ProgramReportTypeSelect";
import { CreateProgramReport } from "../../api/program-reports/createProgramReport";

const CreateProgramReportForm = ({ onCreate, onClose, programs, ...props }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState("");
  const [frequency, setFrequency] = useState("");
  const [method, setMethod] = useState("");
  const [email, setEmail] = useState("");
  const [timeRange, setTimeRange] = useState("");
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [program, setProgram] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitDisabled(true);
    try {
      await CreateProgramReport(
        program,
        email,
        report,
        frequency.value,
        timeRange.value,
        method
      );
      setLoading(false);
      setSubmitDisabled(false);
      onCreate();
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSubmitDisabled(false);
      console.log("Error fetching data: " + err);
    }
  };

  useEffect(() => {
    if (programs.length > 0) {
      setProgram(programs[0].id);
    }

    return () => {
      setProgram("");
    };
  }, [programs]);

  useEffect(() => {
    if (report && frequency && method && email && timeRange) {
      setSubmitDisabled(false);
    } else {
      setSubmitDisabled(true);
    }
  }, [report, frequency, method, email, timeRange]);

  return (
    <Popup size="large" withClose withOverlay onClose={onClose}>
      <form method="post" onSubmit={handleSubmit}>
        <FormHeader title={t("New report subscription form")}></FormHeader>
        <div className="row mt-5">
          {programs.length > 1 && (
            <div className="col-xs-6">
              <FormSection title={t("Program")} required>
                <CustomSelect
                  placeholder={<div>{t("Select program")}</div>}
                  options={programs.map((program) => ({
                    value: program.id,
                    label: program.name,
                  }))}
                  onChange={(option) => {
                    setProgram(option.value);
                    setReport("");
                  }}
                  value={{
                    value: program,
                    label: programs.find((p) => p.id === program)?.name,
                  }}
                />
              </FormSection>
            </div>
          )}
        </div>
        <div className="row mt-5">
          <div className="col-xs-6">
            <FormSection title={t("Report type")} required>
              <ProgramReportTypeSelect
                value={report}
                onChange={(option) => setReport(option.value)}
                program={program}
              />
            </FormSection>
            <FormSection
              title={t("Time frequency")}
              beforeTitle={
                <CustomTooltip
                  message={
                    <ul>
                      <li>
                        <strong>{t("Daily")}:</strong>{" "}
                        {t("Every day at 6 PM UTC")}
                      </li>
                      <li>
                        <strong>{t("Weekly")}:</strong>{" "}
                        {t("Every Sunday at 6 PM UTC")}
                      </li>
                      <li>
                        <strong>{t("Monthly")}:</strong>{" "}
                        {t("Every first of the month at 6 PM UTC")}
                      </li>
                    </ul>
                  }
                />
              }
              required
            >
              <CustomSelect
                placeholder={<div>{t("Select Frequency")}</div>}
                options={[
                  {
                    value: "@daily",
                    label: t("Daily"),
                  },
                  {
                    value: "@weekly",
                    label: t("Weekly"),
                  },
                  {
                    value: "@monthly",
                    label: t("Monthly"),
                  },
                ]}
                onChange={(value) => setFrequency(value)}
                value={frequency}
              />
            </FormSection>
            <FormSection title={t("Reporting method")} required>
              <ReportMethodSelect
                value={method}
                onChange={(option) => setMethod(option.value)}
                program={program}
              />
            </FormSection>
          </div>
          <div className="col-xs-6">
            <FormSection title={t("Email")} required>
              <input
                type="email"
                name="email"
                id="email"
                placeholder={t("Email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormSection>
            <FormSection
              title={t("Time range")}
              beforeTitle={
                <CustomTooltip
                  message={
                    <ul>
                      <li>
                        {t(
                          "You may select a time range up to 93 days in the past from today’s date."
                        )}
                      </li>
                    </ul>
                  }
                />
              }
              required
            >
              <CustomSelect
                placeholder={<div>{t("Select time range")}</div>}
                options={[
                  {
                    value: "yesterday",
                    label: t("Yesterday"),
                  },
                  {
                    value: "previous_7_days",
                    label: t("Previous 7 days"),
                  },
                  {
                    value: "previous_31_days",
                    label: t("Previous 31 days"),
                  },
                  {
                    value: "previous_93_days",
                    label: t("Previous 93 days"),
                  },
                  {
                    value: "all_time",
                    label: t("All time"),
                  },
                ]}
                onChange={(option) => setTimeRange(option)}
                value={timeRange}
              />
            </FormSection>
          </div>
        </div>
        <div className="text-right mt-5">
          <button
            className={`btn btn-primary btn-medium ${
              loading ? "btn-loading" : ""
            }`}
            type="submit"
            name="submit"
            disabled={submitDisabled}
          >
            {loading ? <LoadingIcon /> : ""}
            {t("Submit")}
          </button>
        </div>
        {error && <p className="color-error text-center mt-3">{error}</p>}
      </form>
    </Popup>
  );
};

export default CreateProgramReportForm;
